import React from 'react';

import type { AIAlert, AIAlertVariant } from '@aline/common';
import { aiAlertSchema, tryCatch } from '@aline/common';
import { Typography, useTheme } from '@mui/material';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { makeStyles } from 'tss-react/mui';

import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';

const useStyles = makeStyles()(theme => ({
  contentContainer: {
    flex: '1 1 auto',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  message: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  root: {
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(2),
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

const ALERT_ICONS: Record<AIAlertVariant, React.ComponentType<{ style?: React.CSSProperties }>> = {
  info: InfoIcon,
  warning: WarningIcon,
};

const AlertIcon = ({ variant }: { variant: AIAlertVariant }) => {
  const Icon = ALERT_ICONS[variant];
  return <Icon style={{ height: 20, width: 20 }} />;
};

export const CodeAlertElement = React.memo(function CodeAlertElement({
  children,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const theme = useTheme();
  const code = React.Children.toArray(children).join('');
  const parsedCode = tryCatch(() => JSON.parse(code));
  if (!parsedCode.ok) return null;

  const alertResult = aiAlertSchema.safeParse(parsedCode.result);
  const alertData: AIAlert = alertResult.success
    ? alertResult.data
    : {
        id: 'unknown',
        message: 'An unknown alert occurred.',
        title: 'Alert',
        type: 'alert',
        variant: 'info',
      };
  const borderColor =
    alertData.variant === 'warning' ? theme.palette.warning.main : theme.palette.info.main;

  return (
    <div className={classes.root} style={{ border: `2px solid ${borderColor}` }}>
      <div className={classes.iconContainer}>
        <AlertIcon variant={alertData.variant} />
      </div>
      <div className={classes.contentContainer}>
        <Typography className={classes.title}>{alertData.title}</Typography>
        <Typography className={classes.message}>{alertData.message}</Typography>
      </div>
    </div>
  );
});
